import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ProgressMonitor from '../components/ProgressMonitor';
import ContentEditorModal from '../components/ContentEditorModal';
import { icpAgentAPI, handleAPIError, api } from '../services/api';
import type { JobStatus, Job } from '../types';

type PageState = 'upload' | 'processing' | 'completed' | 'error';

interface ICPResults {
  job: Job;
  output: {
    id: number;
    job_id: number;
    file_path: string | null;
    word_count: number | null;
    generated_at: string | null;
    edited_at: string | null;
    edited_by_name: string | null;
  } | null;
}

interface FactSheet {
  job_id: number;
  label: string;
  completed_at: string | null;
  word_count: number | null;
  has_content: boolean;
  step_name: string | null;
}

export default function ICPAgentPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  const [pageState, setPageState] = useState<PageState>('upload');
  const [jobId, setJobId] = useState<number | null>(null);
  const [results, setResults] = useState<ICPResults | null>(null);
  const [error, setError] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Fact sheets selector
  const [factSheets, setFactSheets] = useState<FactSheet[]>([]);
  const [selectedFactSheet, setSelectedFactSheet] = useState<number | null>(null);
  const [loadingFactSheets, setLoadingFactSheets] = useState(false);

  // Recent ICP jobs
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);

  // Editor modal
  const [editorOpen, setEditorOpen] = useState(false);

  const urlJobId = searchParams.get('job_id');

  useEffect(() => {
    if (projectId) {
      loadFactSheets();
      loadRecentJobs();
    }
  }, [projectId]);

  useEffect(() => {
    if (urlJobId && projectId) {
      const id = parseInt(urlJobId);
      if (!isNaN(id)) {
        viewJob({ id } as Job);
      }
    }
  }, [urlJobId, projectId]);

  const loadFactSheets = async () => {
    if (!projectId) return;
    try {
      setLoadingFactSheets(true);
      const response = await api.get(`/factsheets/project/${projectId}/selector`);
      const sheets = response.data.fact_sheets || [];
      setFactSheets(sheets);
      if (sheets.length > 0 && !selectedFactSheet) {
        setSelectedFactSheet(sheets[0].job_id);
      }
    } catch (err) {
      console.error('Failed to load fact sheets:', err);
      setFactSheets([]);
    } finally {
      setLoadingFactSheets(false);
    }
  };

  const loadRecentJobs = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/projects/${projectId}/jobs?system_type=icpagent&status=completed`);
      setRecentJobs(response.data);
    } catch (err) {
      console.error('Failed to load ICP jobs:', err);
    }
  };

  const viewJob = async (job: Job) => {
    try {
      setJobId(job.id);
      const data = await icpAgentAPI.getResults(job.id);
      setResults(data);
      setPageState('completed');
    } catch (err) {
      setError(handleAPIError(err));
      setPageState('error');
    }
  };

  const handleGenerate = async () => {
    if (!projectId || !selectedFactSheet) {
      setError('Please select a fact sheet first.');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const uploadResponse = await icpAgentAPI.uploadFromFactsAgent(
        parseInt(projectId),
        selectedFactSheet
      );

      // Use job_id from response, fallback to regex parsing
      const newJobId = (uploadResponse as any).job_id
        || parseInt(uploadResponse.message.match(/Job ID: (\d+)/)?.[1] || '0');
      if (!newJobId) {
        throw new Error('Failed to get job ID from upload response');
      }

      setJobId(newJobId);
      await icpAgentAPI.startGeneration(newJobId);
      setPageState('processing');
    } catch (err) {
      setError(handleAPIError(err));
      setPageState('error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleStatusUpdate = useCallback(async (status: JobStatus) => {
    if (status.status === 'completed') {
      if (jobId) {
        try {
          const data = await icpAgentAPI.getResults(jobId);
          setResults(data);
          setPageState('completed');
          loadRecentJobs();
        } catch (err) {
          setError(handleAPIError(err));
          setPageState('error');
        }
      }
    } else if (status.status === 'failed') {
      setError(status.error_message || 'ICP generation failed');
      setPageState('error');
    }
  }, [jobId]);

  const handleReset = () => {
    setPageState('upload');
    setJobId(null);
    setResults(null);
    setError('');
    loadRecentJobs();
  };

  const handleDelete = async () => {
    if (!jobId) return;
    if (!confirm('Are you sure you want to delete this ICP?')) return;

    try {
      await icpAgentAPI.deleteJob(jobId);
      handleReset();
    } catch (err) {
      alert('Failed to delete job: ' + handleAPIError(err));
    }
  };

  const handleEditorSave = async () => {
    if (jobId) {
      try {
        const data = await icpAgentAPI.getResults(jobId);
        setResults(data);
      } catch (err) {
        console.error('Failed to refresh results:', err);
      }
    }
  };

  return (
    <Layout projectId={projectId ? parseInt(projectId) : null}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">ICP Tool</h1>
              <p className="page-subtitle">
                Generate a comprehensive Ideal Customer Profile from your FactsAgent data
                <a href="https://drive.google.com/file/d/1CTIj4DiLE36CnYQhncUlcrjh9H8w4GHm/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                  Video Tutorial
                </a>
              </p>
            </div>
            {pageState === 'completed' && results && (
              <div className="flex items-center gap-3">
                <a
                  href={icpAgentAPI.downloadICP(results.job.id)}
                  download
                  className="btn-primary"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download ICP
                </a>
                <button onClick={handleReset} className="btn-secondary">
                  Generate Another
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Recent ICP Jobs */}
        {recentJobs.length > 0 && pageState === 'upload' && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Generated ICPs ({recentJobs.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentJobs.map((job) => {
                const title = job.settings?.facts_file
                  ? `ICP - ${String(job.settings.facts_file).replace(/\.(txt|json)$/i, '')}`
                  : `ICP ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                return (
                  <div
                    key={job.id}
                    onClick={() => viewJob(job)}
                    className="card hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer group"
                  >
                    <div className="card-body">
                      <div className="flex items-start space-x-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 group-hover:text-brand-600 transition-colors truncate" title={title}>
                            {title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1 flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {new Date(job.completed_at || job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <span className="text-brand-600 text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                          View/Edit
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">Or generate a new ICP below</p>
            </div>
          </div>
        )}

        {/* Upload State */}
        {pageState === 'upload' && (
          <div className="max-w-2xl mx-auto">
            <div className="card">
              <div className="card-body">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Generate ICP</h2>
                <p className="text-gray-500 mb-6">
                  Select a completed FactsAgent fact sheet to generate an Ideal Customer Profile document.
                </p>

                {/* Fact Sheet Selector */}
                <div className="mb-6">
                  <label className="label">Select Fact Sheet</label>
                  {loadingFactSheets ? (
                    <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                      <span className="spinner"></span>
                      Loading fact sheets...
                    </div>
                  ) : factSheets.length === 0 ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                      No completed fact sheets found. Please run FactsAgent first to generate a fact sheet.
                    </div>
                  ) : (
                    <select
                      value={selectedFactSheet || ''}
                      onChange={(e) => setSelectedFactSheet(parseInt(e.target.value))}
                      className="input"
                      disabled={isGenerating}
                    >
                      <option value="" disabled>Select a fact sheet...</option>
                      {factSheets.map((fs) => (
                        <option key={fs.job_id} value={fs.job_id}>
                          {fs.label || `Fact Sheet`} — Job #{fs.job_id}
                          {fs.word_count ? ` (${fs.word_count.toLocaleString()} words)` : ''}
                          {fs.completed_at ? ` — ${new Date(fs.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !selectedFactSheet || factSheets.length === 0}
                  className="btn-primary w-full"
                >
                  {isGenerating ? (
                    <>
                      <span className="spinner"></span>
                      Creating job...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Generate ICP
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Info Card */}
            <div className="mt-6 card bg-purple-50 border-purple-200">
              <div className="card-body">
                <h3 className="text-lg font-semibold text-purple-900 mb-2">What gets generated?</h3>
                <p className="text-purple-700 mb-3">A comprehensive ICP document including:</p>
                <ul className="list-disc list-inside space-y-1.5 text-sm text-purple-700">
                  <li>Core Values and Core Focus (Purpose + Niche)</li>
                  <li>Marketing Strategy (Target Market, Geographic Focus, Psychographics)</li>
                  <li>Three Uniques differentiating the facility</li>
                  <li>Proven Process (step-by-step client journey)</li>
                  <li>Guarantee statement</li>
                  <li>2 detailed Ideal Client Avatars (22 subsections each)</li>
                  <li>Data-Backed Statistics</li>
                  <li>How to Use This ICP guide</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Processing State */}
        {pageState === 'processing' && jobId && (
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Generating ICP — Job #{jobId}
                  </h2>
                  <p className="text-gray-500">
                    Your ICP document is being generated. This typically takes 2-4 minutes.
                  </p>
                </div>
                <ProgressMonitor
                  jobId={jobId}
                  systemType="icpagent"
                  onStatusUpdate={handleStatusUpdate}
                />
              </div>
            </div>
          </div>
        )}

        {/* Completed State */}
        {pageState === 'completed' && results && (
          <div className="max-w-4xl mx-auto">
            <div className="card mb-6">
              <div className="card-header bg-success-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-success-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-success-900">ICP Generated Successfully</h2>
                    <p className="text-sm text-success-700">
                      {results.output?.word_count
                        ? `${results.output.word_count.toLocaleString()} words`
                        : 'Document ready'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ICP Output Card */}
            {results.output && (
              <div
                className="card hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer group mb-6"
                onClick={() => setEditorOpen(true)}
              >
                <div className="card-body">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-brand-600 transition-colors">
                          Ideal Customer Profile
                        </h3>
                        {results.output.word_count && (
                          <p className="text-sm text-gray-500 mt-1">
                            {results.output.word_count.toLocaleString()} words
                          </p>
                        )}
                        {results.output.edited_at && results.output.edited_by_name && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Edited by {results.output.edited_by_name}</span>
                          </div>
                        )}
                        <span className="text-brand-600 text-sm font-medium mt-2 inline-flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Click to View/Edit
                        </span>
                      </div>
                    </div>
                    <a
                      href={icpAgentAPI.downloadICP(results.job.id)}
                      download
                      onClick={(e) => e.stopPropagation()}
                      className="btn-ghost btn-sm"
                      title="Download"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Job Management */}
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Job Management</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Job ID: {results.job.id}
                      {results.job.completed_at && ` • Completed: ${new Date(results.job.completed_at).toLocaleString()}`}
                      {results.job.settings?.facts_file && ` • Source: ${String(results.job.settings.facts_file)}`}
                    </p>
                  </div>
                  <button onClick={handleDelete} className="btn-danger">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {pageState === 'error' && (
          <div className="max-w-2xl mx-auto">
            <div className="card border-danger-200 bg-danger-50">
              <div className="card-body text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-danger-100 flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-danger-900 mb-2">Error</h2>
                <p className="text-danger-700 mb-4">{error}</p>
                <button onClick={handleReset} className="btn-primary">
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content Editor Modal */}
      {editorOpen && jobId && (
        <ContentEditorModal
          apiEndpoint={`/icpagent/output/${jobId}`}
          contentTitle="Ideal Customer Profile"
          isOpen={editorOpen}
          onClose={() => setEditorOpen(false)}
          onSave={handleEditorSave}
        />
      )}
    </Layout>
  );
}
